import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";
import { useToast } from "../../../context/toastContext";
import { useConfirm } from "../../../context/confirmContext";
import { DISPUTE_REASONS } from "../../../constants/supplyChain";

const STATUSES = [
    { value: "open", label: "Open" },
    { value: "upheld", label: "Upheld" },
    { value: "dismissed", label: "Dismissed" },
];

const RESOLUTIONS = [
    { value: "dismiss", label: "Keep listing", help: "The listing stands; the business sees it again.", variant: "primary" },
    { value: "unlink", label: "Not them — unlink", help: "The reporter named a different business: the listing points to a CRM lead by name instead.", variant: "primary" },
    { value: "end", label: "Remove listing", help: "There is no such relationship: the listing is ended.", variant: "danger" },
];

const reasonLabel = (value) => DISPUTE_REASONS.find((r) => r.value === value)?.label || value;

/**
 * Admin review of supply-chain disputes: a DYC business says another business listed it
 * wrongly as a vendor/customer. Disputed listings are hidden from the disputing business
 * until resolved here. Businesses not yet on DYC are worked in the CRM (source
 * "Supply chain reporting") — this tab replaced Prospective Operators.
 */
const SupplyChainDisputes = () => {
    const toast = useToast();
    const confirm = useConfirm();
    const [status, setStatus] = useState("open");
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ disputes: [], total: 0, pageSize: 50 });
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        apiClient
            .get(`/admin/supply-chain/disputes?status=${status}&page=${page}`)
            .then((res) => setData(res.data))
            .catch(() => toast.error("Failed to load disputes."))
            .finally(() => setLoading(false));
    }, [status, page, toast]);

    useEffect(() => {
        load();
    }, [load]);

    const resolve = async (dispute, resolution) => {
        const r = RESOLUTIONS.find((x) => x.value === resolution);
        const ok = await confirm({
            title: r.label,
            message: `${dispute.reporterCompany} → ${dispute.partnerCompany || dispute.partnerName}. ${r.help}`,
            confirmLabel: r.label,
            variant: r.variant,
        });
        if (!ok) return;
        setBusyId(dispute.id);
        try {
            await apiClient.put(`/admin/supply-chain/disputes/${dispute.id}`, { resolution });
            toast.success("Dispute resolved. Both businesses were notified.");
            load();
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to resolve dispute.");
        } finally {
            setBusyId(null);
        }
    };

    const pages = Math.max(1, Math.ceil(data.total / data.pageSize));

    return (
        <div>
            <p className="admin-license-meta" style={{ display: "block", marginBottom: "var(--space-3)" }}>
                Businesses not yet on DYC are worked as CRM leads —{" "}
                <Link to="/CRMPanel/outbound?source=supply_chain">view supply-chain leads in the CRM</Link>.
            </p>
            <div className="admin-sub-tab-bar" role="tablist" aria-label="Dispute status">
                {STATUSES.map((s) => (
                    <button
                        key={s.value}
                        type="button"
                        role="tab"
                        aria-selected={status === s.value}
                        className={`admin-sub-tab-btn ${status === s.value ? "active" : ""}`}
                        onClick={() => {
                            setStatus(s.value);
                            setPage(1);
                        }}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <Loading />
            ) : data.disputes.length === 0 ? (
                <p className="admin-table-empty">No {status} disputes.</p>
            ) : (
                <div className="admin-table-scroll">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Reported by</th>
                            <th>Listed as</th>
                            <th>Disputed by</th>
                            <th>Reason</th>
                            <th>Date</th>
                            {status === "open" && <th>Resolve</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {data.disputes.map((d) => (
                            <tr key={d.id}>
                                <td>{d.reporterCompany || d.reporterLicenseNo}</td>
                                <td>{d.direction === "upstream" ? "their vendor" : "their customer"}</td>
                                <td>{d.partnerCompany || d.partnerName}</td>
                                <td>
                                    {reasonLabel(d.disputeReason)}
                                    {d.disputeNote && <div className="admin-license-meta">“{d.disputeNote}”</div>}
                                </td>
                                <td>{d.disputedAt ? new Date(d.disputedAt).toLocaleDateString() : ""}</td>
                                {status === "open" && (
                                    <td>
                                        <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap" }}>
                                            {RESOLUTIONS.map((r) => (
                                                <button
                                                    key={r.value}
                                                    type="button"
                                                    className={`admin-action-btn${r.variant === "danger" ? " disable" : ""}`}
                                                    disabled={busyId === d.id}
                                                    title={r.help}
                                                    onClick={() => resolve(d, r.value)}
                                                >
                                                    {r.label}
                                                </button>
                                            ))}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}

            {pages > 1 && (
                <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", marginTop: "var(--space-3)" }}>
                    <button type="button" className="admin-action-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                        Previous
                    </button>
                    <span>
                        Page {page} of {pages}
                    </span>
                    <button type="button" className="admin-action-btn" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default SupplyChainDisputes;
